import classNames from 'classnames';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import logHeatmapFilterMenuTapped from '@alltrails/amplitude/events/logHeatmapFilterMenuTapped';
import Typography from '@alltrails/core/components/Typography';
import LoadingDots from '@alltrails/denali/components/LoadingDots';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import GradientLayerLegend from '@alltrails/maps/components/GradientLayerLegend/GradientLayerLegend';
import LayerLegendContainer from '@alltrails/maps/components/LayerLegendContainer/LayerLegendContainer';
import { useGetHeatmapOptionsDataQuery } from '../../redux/heatmapsApi';
import { updateAvailableOptions, updateCurrentlySelectedOptions, updateIsHeatmapFilterModalOpen, updateUrlTemplate } from '../../redux/heatmapsSlice';
import { useAppDispatch, useAppSelector } from '../../redux/helpers';
import { HeatmapActivityOption } from '../../types/HeatmapActivity';
import { HeatmapOptionKey } from '../../types/HeatmapOptionsData';
import { HeatmapTimeRangeOption } from '../../types/HeatmapTimeRange';
import styles from './HeatmapLegend.module.scss';

type Props = {
  isOpen: boolean;
};

const HeatmapLegend = ({ isOpen }: Props) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const { baseStyleId, currentlySelectedOptions, isHeatmapFilterModalOpen } = useAppSelector(state => ({
    baseStyleId: state.map.baseStyleId,
    currentlySelectedOptions: state.heatmaps.currentlySelectedOptions,
    isHeatmapFilterModalOpen: state.heatmaps.isHeatmapFilterModalOpen
  }));
  const gradientBackgroundStyle =
    baseStyleId === 'alltrailsSatellite' || baseStyleId === 'googleSatellite'
      ? 'var(--heatmap-gradient-dark, linear-gradient(90deg, var(--dataviz-heatmaps-1, rgba(88, 1, 101, 0.20)) 0%, var(--dataviz-heatmaps-2, rgba(88, 1, 101, 0.30)) 25%, var(--dataviz-heatmaps-3, rgba(136, 2, 176, 0.60)) 50%, var(--dataviz-heatmaps-4, rgba(183, 120, 252, 0.70)) 75%, var(--dataviz-heatmaps-5, rgba(255, 255, 255, 0.80)) 100%), rgba(88, 1, 101, 0.60))'
      : 'linear-gradient(90deg, var(--dataviz-heatmaps-1, rgba(231, 83, 253, 0.20)) 0%, var(--dataviz-heatmaps-2, rgba(231, 83, 253, 0.30)) 25%, var(--dataviz-heatmaps-3, rgba(231, 83, 253, 0.60)) 50%, var(--dataviz-heatmaps-4, rgba(171, 70, 251, 0.70)) 75%, var(--dataviz-heatmaps-5, rgba(113, 57, 249, 0.80)) 100%), var(--text-light-secondary, rgba(255, 255, 255, 0.60))';

  const dispatch = useAppDispatch();
  const { data, isLoading } = useGetHeatmapOptionsDataQuery(undefined, {
    skip: !isOpen
  });

  const handleClick = useCallback(() => {
    logHeatmapFilterMenuTapped();
    dispatch(updateIsHeatmapFilterModalOpen(true));
  }, [dispatch]);

  useEffect(() => {
    const options = data?.options;
    if (options && options.length > 0) {
      dispatch(updateAvailableOptions(options));
      options.forEach(option => {
        if (option.values.length > 0) {
          dispatch(
            updateCurrentlySelectedOptions({ [option.key]: option.values[0] } as Record<
              HeatmapOptionKey,
              HeatmapActivityOption | HeatmapTimeRangeOption
            >)
          );
        }
      });
    }
    if (data?.urlTemplate) {
      dispatch(updateUrlTemplate(data.urlTemplate));
    }
  }, [data, dispatch]);

  return (
    <LayerLegendContainer className={styles.heatmapLegendContainer} isOpen={isOpen} testIdPrefix="heatmap-legend">
      <GradientLayerLegend
        gradientBackgroundStyle={gradientBackgroundStyle}
        ticks={[
          [0, <FormattedMessage key="low" defaultMessage="Low" />],
          [1, <FormattedMessage key="high" defaultMessage="High" />]
        ]}
      />
      <div className={styles.divider} />
      <button
        className={classNames(styles.heatmapLegendButton, {
          [styles.loading]: isLoading
        })}
        onClick={handleClick}
        aria-label={
          currentlySelectedOptions?.['activity']?.name
            ? currentlySelectedOptions.activity.name
            : intl.formatMessage({ defaultMessage: 'Select activity' })
        }
      >
        {currentlySelectedOptions?.['activity']?.name ? (
          <>
            <Typography component="div" variant="text50" color="inverse" className={styles.activityName}>
              {currentlySelectedOptions?.activity.name}
            </Typography>
            <ChevronSm orientation={isMobile ? 'down' : isHeatmapFilterModalOpen ? 'up' : 'down'} color="--color-text-light" />
          </>
        ) : (
          <LoadingDots />
        )}
      </button>
    </LayerLegendContainer>
  );
};

export default HeatmapLegend;
